import {
  FormStatusInfo,
  leavePageReducer,
  State as LeavePageState
} from '@app/core/leave-page-handler/reducers/leave-page';
import { Country, GridDisplaySettings } from '@app/models';
import { DossierSearchType, UserFilter, UserLabel } from '@app/pcs/models';
import * as fromBackButton from '@app/pcs/reducer/back-button';
import * as fromCalendarEdit from '@app/pcs/reducer/calendar';
import * as fromCountries from '@app/pcs/reducer/countries';
import * as fromFilterEdit from '@app/pcs/reducer/filter-edit';
import * as fromFilters from '@app/pcs/reducer/filters';
import * as fromLabelEdit from '@app/pcs/reducer/label-edit';
import * as fromLabels from '@app/pcs/reducer/labels';
import * as fromLanguages from '@app/pcs/reducer/languages';
import * as fromRecentActivity from '@app/pcs/reducer/recent-activity';
import { getVisibleFilters, isDeadlineFilter, isNotDeadlineFilter } from '@app/pcs/util/user-filter';
import { getLabelId, getLabelName } from '@app/pcs/util/user-label';
import * as fromRoot from '@app/reducer';
import { getQueryParams } from '@app/reducer';
import { hasValue } from '@app/util';
import { getValdrSelectors } from '@app/valdr';
import * as fromValdr from '@app/valdr/reducers/valdr';
import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as R from 'ramda';

import * as fromGridConfig from './grid-config';
import * as fromShareFilterLabel from './share-filter-label';

export interface PcsState {
  valdrConstraints: fromValdr.State;
  leavePage: LeavePageState;
  gridConfig: fromGridConfig.State;
  backButton: fromBackButton.State;
  labels: fromLabels.State;
  filters: fromFilters.State;
  recentActivity: fromRecentActivity.State;
  filterEdit: fromFilterEdit.State;
  labelEdit: fromLabelEdit.State;
  shareFilterLabel: fromShareFilterLabel.State;
  calendarEdit: fromCalendarEdit.State;
  countries: fromCountries.State;
  languages: fromLanguages.State;
}

export interface State extends fromRoot.State {
  pcs: PcsState;
}

export function valdrReducer(state, action) {
  return fromValdr.valdrReducer(state, action);
}

export function gridConfigReducer(state, action) {
  return fromGridConfig.reducer(state, action);
}

export function backButtonReducer(state, action) {
  return fromBackButton.reducer(state, action);
}

export function labelsReducer(state, action) {
  return fromLabels.reducer(state, action);
}

export function filtersReducer(state, action) {
  return fromFilters.reducer(state, action);
}

export function recentActivityReducer(state, action) {
  return fromRecentActivity.reducer(state, action);
}

export function filterEditReducer(state, action) {
  return fromFilterEdit.reducer(state, action);
}

export function labelEditReducer(state, action) {
  return fromLabelEdit.reducer(state, action);
}

export function shareFilterLabelReducer(state, action) {
  return fromShareFilterLabel.reducer(state, action);
}

export function calendarEditReducer(state, action) {
  return fromCalendarEdit.reducer(state, action);
}

export function countriesReducer(state, action) {
  return fromCountries.reducer(state, action);
}

export function languagesReducer(state, action) {
  return fromLanguages.reducer(state, action);
}

export const reducers: ActionReducerMap<PcsState> = {
  valdrConstraints: valdrReducer,
  leavePage: leavePageReducer,
  gridConfig: gridConfigReducer,
  backButton: backButtonReducer,
  labels: labelsReducer,
  filters: filtersReducer,
  recentActivity: recentActivityReducer,
  filterEdit: filterEditReducer,
  labelEdit: labelEditReducer,
  shareFilterLabel: shareFilterLabelReducer,
  calendarEdit: calendarEditReducer,
  countries: countriesReducer,
  languages: languagesReducer
};

export const selectPcsState = createFeatureSelector<PcsState>('pcs');

// Valdr
export const getValdrState = createSelector(
  selectPcsState,
  s => s.valdrConstraints
);

export const {
  getValdrConstraints,
  getConstraintsForTypes
} = getValdrSelectors(getValdrState);

// Leave page
export const getLeavePageState = createSelector(
  selectPcsState,
  state => state.leavePage
);

const getLeavePageFormStatusInfo = createSelector(
  getLeavePageState,
  state => state.formStatusInfo
);

export const getLeavePageFormDirty = createSelector(
  getLeavePageFormStatusInfo,
  (formStatusInfo: FormStatusInfo) => hasValue(formStatusInfo) && formStatusInfo.formDirty
);

export const getLeavePageNextUrl = createSelector(
  getLeavePageState,
  state => state.nextPageUrl
);

// Back Button
export const getBackButtonState = createSelector(
  selectPcsState,
  state => state.backButton
);

export const getAllDossiers = createSelector(
  getBackButtonState,
  fromBackButton.getAllDossiers
);

export const getCurrentDossierIndex = createSelector(
  getBackButtonState,
  fromBackButton.getCurrentIndex
);

export const getNextDossier = createSelector(
  getAllDossiers,
  getCurrentDossierIndex,
  (allDossiers, currentIndex) =>
    currentIndex < allDossiers.length - 1
      ? R.nth(currentIndex + 1, allDossiers)
      : null
);

export const getPreviousDossier = createSelector(
  getAllDossiers,
  getCurrentDossierIndex,
  (allDossiers, currentIndex) =>
    currentIndex > 0
      ? R.nth(currentIndex - 1, allDossiers)
      : null
);

// Labels
export const getLabelsState = createSelector(
  selectPcsState,
  state => state.labels
);

export const getLabels = createSelector(
  getLabelsState,
  fromLabels.getLabels
);

export const getLabelSearch = createSelector(
  getLabelsState,
  fromLabels.getLabelSearch
);

export const getSelectedLabelIds = createSelector(
  getLabelsState,
  fromLabels.getSelectedLabelIds
);

export const getLabelsWithSelection = createSelector(
  getLabels,
  getSelectedLabelIds,
  (userLabels, selectedIds) => {
    const withSelection = (userLabel: UserLabel) =>
      R.mergeRight(userLabel, {
        selected: R.any(R.equals(getLabelId(userLabel)), selectedIds)
      });

    return R.map(withSelection, userLabels);
  }
);

export const getSelectedLabels = createSelector(
  getLabelsWithSelection,
  (labelsWithSelection) =>
    R.filter<UserLabel>(R.prop('selected'), labelsWithSelection)
);

export const hasSelectedLabels = createSelector(
  getSelectedLabels,
  (selectedLabels) => hasValue(selectedLabels)
);

export const getLabelsFiltered = createSelector(
  getLabels,
  getLabelSearch,
  (labels, labelSearch) =>
    R.filter(R.compose(R.includes(R.toLower(labelSearch)), R.toLower, getLabelName), labels)
);

// Filters
export const getFiltersState = createSelector(
  selectPcsState,
  state => state.filters
);

export const getAllFilters = createSelector(
  getFiltersState,
  fromFilters.getFilters
);

export const getSelectedFilterId = createSelector(
  getFiltersState,
  fromFilters.getSelectedFilterId
);

export const getSelectedFilterName = createSelector(
  getFiltersState,
  fromFilters.getSelectedFilterName
);

export const getSelectedFilterTtp = createSelector(
  getFiltersState,
  fromFilters.getSelectedFilterTtp
);

const filtersWithSelection = (filters, selectedId) => {
  const withSelection = (filter: UserFilter) => R.mergeRight(filter, {
    selected: R.equals(selectedId, filter.filter.id)
  });

  return R.map(withSelection, filters);
};

export const getVisibleUserFilters = createSelector(
  getAllFilters, (filters) => getVisibleFilters(filters)
);

export const getUserFilters = createSelector(
  getVisibleUserFilters,
  getSelectedFilterId,
  (filters, selectedId) => {
    const userFilters = R.filter<UserFilter>(isNotDeadlineFilter, filters);
    return filtersWithSelection(userFilters, selectedId);
  }
);

export const getAllUserFilters = createSelector(
  getAllFilters,
  (filters: UserFilter[]) => R.filter(isNotDeadlineFilter, filters)
);

export const getDeadlineUserFilters = createSelector(
  getVisibleUserFilters,
  getSelectedFilterId,
  (filters, selectedId) => {
    const deadlineFilters = R.filter<UserFilter>(isDeadlineFilter, filters);
    return filtersWithSelection(deadlineFilters, selectedId);
  }
);

export const getDisplayFilterForm = createSelector(
  getFiltersState,
  fromFilters.getDisplayFilterForm
);

export const getSelectedFilter = createSelector(
  getVisibleUserFilters,
  getSelectedFilterId,
  (filters, selectedId) =>
    R.find<UserFilter>(R.pathEq(['filter', 'id'], selectedId), filters)
);

export const getDisplaySettingsForSelectedFilter = createSelector(
  getSelectedFilter,
  (selectedFilter): GridDisplaySettings => selectedFilter && JSON.parse(selectedFilter.displaySettings)
);

// Recent Activity
export const getRecentActivityState = createSelector(
  selectPcsState,
  state => state.recentActivity
);

export const getRecentActivitySelected = createSelector(
  getRecentActivityState,
  fromRecentActivity.getRecentActivitySelected
);

// Search
export const getDossierSearchState = createSelector(
  selectPcsState,
  state => state.gridConfig
);

export const getDossierSearchType = createSelector(
  getDossierSearchState,
  fromGridConfig.getDossierSearchType
);

export const getDashboardSearchType = createSelector(
  getDossierSearchState,
  fromGridConfig.getDashboardSearchType
);

export const getUserGridType = createSelector(
  getDossierSearchState,
  fromGridConfig.getUserGridType
);

export const getUserGridConfiguration = createSelector(
  getDossierSearchState,
  fromGridConfig.getConfiguration
);

export const getDossierSearchTitle = createSelector(
  getDossierSearchType,
  getSelectedFilterName,
  getSelectedFilterTtp,
  (searchType, filterName, ttpId): { title: string; params: any[] } => {
    const title = R.cond([
      [R.equals(DossierSearchType.RECENT_ACTIVITY), R.always('dashboard.filter.recentActivity')],
      [R.equals(DossierSearchType.LABELS), R.always('label.labels')],
      [R.equals(DossierSearchType.QUICK_SEARCH), R.always('dossiersearch.quickSearch')],
      [R.equals(DossierSearchType.ADVANCED_SEARCH), R.always('general.action.search')],
      [R.equals(DossierSearchType.BULK_ACCEPTANCE_INDICATOR_EDIT), R.always('navigation.bulkEditing.acceptanceIndicator')],
      [R.equals(DossierSearchType.BULK_PERMANENT_ACCEPTANCE), R.always('navigation.bulkEditing.permanentAcceptance')],
      [R.equals(DossierSearchType.BULK_DOSSIER_PROMOTION), R.always('navigation.bulkEditing.dossierPromotion')],
      [R.equals(DossierSearchType.CARRY_FORWARD), R.always('carryForward.title')],
      [R.equals(DossierSearchType.FILTER), R.always(filterName)]
    ])(searchType);

    const params = ttpId ? [ttpId] : [];

    return {title, params};
  }
);

export const getBackLink = createSelector(
  getDossierSearchType,
  R.cond([
    [R.equals(DossierSearchType.ADVANCED_SEARCH), R.always('app/dashboard/advanced-search')],
    [R.equals(DossierSearchType.BULK_DOSSIER_PROMOTION), R.always('app/bulk/dossier-promotion')],
    [R.equals(DossierSearchType.BULK_ACCEPTANCE_INDICATOR_EDIT), R.always('app/bulk/acceptance-indicator')],
    [R.equals(DossierSearchType.BULK_PERMANENT_ACCEPTANCE), R.always('app/bulk/permanent-acceptance')],
    [R.equals(DossierSearchType.CARRY_FORWARD), R.always('app/bulk/carry-forward/search')],
    [R.T, R.always('app/dashboard')],
  ])
);

// Filter edit
const getFilterEditState = createSelector(
  selectPcsState,
  (state: PcsState) => state.filterEdit
);

export const getFilterForm = createSelector(
  getFilterEditState,
  (state: fromFilterEdit.State) => state.form
);

// Label edit
const getLabelEditState = createSelector(
  selectPcsState,
  (state: PcsState) => state.labelEdit
);

export const getLabelForm = createSelector(
  getLabelEditState,
  (state: fromLabelEdit.State) => state.formState
);

export const getLabelFormDisplayed = createSelector(
  getLabelEditState,
  (state: fromLabelEdit.State) => state.displayEditForm
);

// Share filter label state
const getShareFilterLabelState = createSelector(
  selectPcsState,
  (state: PcsState) => state.shareFilterLabel
);

export const getEligibleUsersForSharing = createSelector(
  getShareFilterLabelState,
  (state: fromShareFilterLabel.State) => state.eligibleUsersForSharing
);

export const getSharedUsers = createSelector(
  getShareFilterLabelState,
  (state: fromShareFilterLabel.State) => state.sharedUsers
);

export const getSharedObject = createSelector(
  getShareFilterLabelState,
  (state: fromShareFilterLabel.State) => state.sharedObject
);

export const getSharedObjectType = createSelector(
  getSharedObject,
  R.prop('sharedObjectType')
);

export const getDisplayShareForm = createSelector(
  getShareFilterLabelState,
  (state: fromShareFilterLabel.State) => state.displayShareForm
);

// Calendar edit
export const getCalendarEditState = createSelector(
  selectPcsState,
  (state: PcsState) => state.calendarEdit
);

export const getCalendar = createSelector(
  getCalendarEditState,
  fromCalendarEdit.getCalendar
);

export const getSelectableTrafficPeriods = createSelector(
  getCalendarEditState,
  fromCalendarEdit.getTrafficPeriods
);

// Countries
export const getCountriesState = createSelector(
  selectPcsState,
  state => state.countries
);

export const getCountries = createSelector(
  getCountriesState,
  fromCountries.getCountries
);

export const getSelectedCountry = createSelector(
  getCountries,
  getQueryParams,
  (countries,{countryId}) => R.find(R.propEq('id',countryId), countries)
);

export const getCountriesWithUicCodes = createSelector(
  getCountries,
  (countries) => R.filter((country: Country) => hasValue(country.uicNetCode), countries)
);

// Languages
export const getLanguagesState = createSelector(
  selectPcsState,
  state => state.languages
);

export const getLanguages = createSelector(
  getLanguagesState,
  fromLanguages.getLanguages
);
